import { useCreateOfflineAccountMutation } from '@/store/accountsApi';
import CategoryUtility, { ClassificationItem } from './categories.utility';
import { type AccountDetailsFormState } from './ManualAccountDetails/useAccountDetailsForm';
import { HistoricalValuesFormState } from './ManualHistoricalValues/useHistoricalValuesForm';
import { PortfolioDiversificationFormState } from './ManualPortfolioDiversification/usePortfolioDiversificationForm';
import { useState } from 'react';
import { type RealEstateDetailsFormState } from './ManualRealEstateDetails/useRealEstateDetailsForm';
import { DiversificationScheme } from '@/store/diversificationsApi';
import { ClassificationIds } from '@/utils/enums';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { deriveHistoricalValueRanges } from './ManualHistoricalValues/historicalValuesRanges';
import { useAccountTagCatalogOptions } from '@/hooks/useAccountTagCatalogOptions';
import { readFormSelectedTagIds } from './accountFormTags';
import {
  type OfflineAccountDiversification,
  type OfflineCreationPayload,
} from './offlineAccountPayload';

export type {
  OfflineAccountDiversification,
  OfflineAccountPayload,
  OfflineCreationPayload,
  OfflineRealEstateAccountPayload,
} from './offlineAccountPayload';

function logManualAccountSaveFailure(
  context: Record<string, unknown>,
  error: unknown,
) {
  if (!import.meta.env.DEV) return;
  const isFetchBaseQueryError = (e: unknown): e is FetchBaseQueryError =>
    typeof e === 'object' && e !== null && 'status' in e;

  console.error('[manual-add-account] createOfflineAccount failed', {
    ...context,
    error,
    ...(isFetchBaseQueryError(error)
      ? { status: error.status, data: error.data }
      : {}),
  });
}

export function buildOfflineAccountDiversifications(values: {
  Category: DiversificationScheme | null;
  SubCategory: DiversificationScheme | null;
  Detail: DiversificationScheme | null;
}): OfflineAccountDiversification[] {
  const { Category, SubCategory, Detail } = values;

  if (!Category) return [];

  const childDiversifications: OfflineAccountDiversification[] = [];

  if (SubCategory) {
    childDiversifications.push({
      ...SubCategory,
      Percent: 100,
      ChildDiversifications: Detail
        ? [
            {
              ...Detail,
              Percent: 100,
              ChildDiversifications: [],
            },
          ]
        : [],
    });
  } else if (Detail) {
    // Some category trees do not have an intermediate sub-category level.
    // In that case, persist the selected detail directly under the top-level category.
    childDiversifications.push({
      ...Detail,
      Percent: 100,
      ChildDiversifications: [],
    });
  }

  return [
    {
      ...Category,
      Percent: 100,
      ChildDiversifications: childDiversifications,
    },
  ];
}

const useAccountSave = () => {
  const [success, setSuccess] = useState(false);

  const updateSuccess = (success: boolean) => {
    setSuccess(success);
  };

  const accountTypeValues = (accountType: ClassificationItem) => {
    return {
      ClassificationId: accountType.ClassificationId,
      TopClassificationId: accountType.isAsset
        ? ClassificationIds.ASSET
        : ClassificationIds.LIABILITY,
    };
  };

  const [createOfflineAccount] = useCreateOfflineAccountMutation();
  const { userTagIdSet } = useAccountTagCatalogOptions();

  const accountDetailsValues = (
    accountDetailsForm: AccountDetailsFormState,
  ) => {
    const {
      AccountBalance,
      Notes,
      Name,
      AccountNumber,
      AccountSubType,
      MonthlyPaymentAmount,
      InitialLoanAmount,
      InterestRate,
    } = accountDetailsForm.values;

    return {
      AccountNumber,
      Balance: Number(AccountBalance),
      InitialLoanAmount,
      InterestRate,
      Memo: Notes,
      MonthlyPaymentAmount,
      Name,
      SubClassificationId: Number(AccountSubType),
      IsRealEstate: false,
      UpdateAutomatically: false,
    };
  };

  const portfolioDiversificationValues = (
    portfolioDiversificationForm: PortfolioDiversificationFormState,
  ) => {
    return {
      Diversifications: buildOfflineAccountDiversifications(
        portfolioDiversificationForm.values,
      ),
    };
  };

  const historicalValues = (
    historicalValuesForm: HistoricalValuesFormState,
  ) => {
    const points = historicalValuesForm.values.HistoricalValues.filter(
      (x) =>
        x.EffectiveDate &&
        x.EffectiveDate.trim() !== '' &&
        x.Balance.trim() !== '',
    ).map((x) => ({
      effectiveDate: x.EffectiveDate,
      value: Number(x.Balance),
    }));

    const { ranges } = deriveHistoricalValueRanges(points);

    return {
      DidHistoricalChange: historicalValuesForm.values.DidHistoricalChange, // always set to true in legacy.
      HistoricalValues: ranges,
    };
  };

  const realEstateValues = (
    accountType: ClassificationItem,
    realEstateDetailsForm: RealEstateDetailsFormState,
  ) => {
    const {
      Name,
      Notes,
      AccountSubType,
      Address,
      PropertyValue,
      Zpid,
      ClassificationId,
    } = realEstateDetailsForm.values;

    const addressParts = (Address || '').split(',');

    return {
      AccountNumber: '', // EUD-17: No longer visible on screen.
      Balance: Number(PropertyValue),
      PropertyValue: Number(PropertyValue),
      CityStateZip: addressParts.length
        ? addressParts.slice(1).join(',').trim()
        : null,
      IsRealEstate: CategoryUtility.isRealEstate(accountType.ClassificationId),
      UpdateAutomatically: false, // Not in requirements.
      Zpid,
      Name,
      Memo: Notes,
      Address: addressParts.length ? addressParts[0].trim() : null,
      SubClassificationId: Number(AccountSubType),
      ClassificationId: Number(ClassificationId),
      TopClassificationId: accountType.isAsset
        ? ClassificationIds.ASSET
        : ClassificationIds.LIABILITY,
    };
  };

  const save = async ({
    accountType,
    accountDetailsForm,
    historicalValuesForm,
    portfolioDiversificationForm,
    realEstateDetailsForm,
  }: {
    accountType: ClassificationItem;
    accountDetailsForm: AccountDetailsFormState;
    historicalValuesForm: HistoricalValuesFormState;
    portfolioDiversificationForm: PortfolioDiversificationFormState;
    realEstateDetailsForm: RealEstateDetailsFormState;
  }) => {
    let payload: OfflineCreationPayload | undefined;
    try {
      const accountDetails = CategoryUtility.isRealEstate(
        accountType.ClassificationId,
      )
        ? realEstateValues(accountType, realEstateDetailsForm)
        : accountDetailsValues(accountDetailsForm);

      const selectedTagIds = readFormSelectedTagIds(
        CategoryUtility.isRealEstate(accountType.ClassificationId),
        realEstateDetailsForm.values,
        accountDetailsForm.values,
      );
      const genericTagIds = selectedTagIds.filter((id) => userTagIdSet.has(id));

      payload = {
        ...accountTypeValues(accountType),
        ...accountDetails,
        ...portfolioDiversificationValues(portfolioDiversificationForm),
        ...historicalValues(historicalValuesForm),
      };

      if (genericTagIds.length > 0) {
        // POST /api/investor/offlineAccounts applies TagIds via ApplyUserTagMaps.
        payload.TagIds = genericTagIds;
      }

      await createOfflineAccount(payload).unwrap();
    } catch (e: unknown) {
      logManualAccountSaveFailure(
        {
          accountTypeName: accountType.Name,
          accountTypeUiKey: accountType.uiKey,
          payloadSummary: payload
            ? {
                TopClassificationId: payload.TopClassificationId,
                ClassificationId: payload.ClassificationId,
                SubClassificationId: payload.SubClassificationId,
                Name: payload.Name,
                Balance: payload.Balance,
              }
            : undefined,
        },
        e,
      );
      const wrapped = new Error('Unable to save.');
      (wrapped as Error & { cause: unknown }).cause = e;
      throw wrapped;
    }
  };

  return {
    save,
    success,
    updateSuccess,
  };
};

export default useAccountSave;
